import React from 'react';
import TemplateThumbnail from './TemplateThumbnail';
import { resolveDesignThumbnail } from '../../utils/designThumbnail';

/**
 * Design card preview — a thin adapter over the canonical TemplateThumbnail
 * pipeline so My Designs, Dashboard → Recent Designs, and Saved Files render
 * thumbnails IDENTICALLY to the Templates page. There is intentionally no
 * second preview system: this just maps a `design` row onto the shape
 * TemplateThumbnail expects, then delegates.
 *
 * What TemplateThumbnail gives us (and why the old fillContainer crop is gone):
 *   - Container is width:100% with aspectRatio = canvas w/h, so the card area
 *     matches the design's real proportions.
 *   - PageThumbnailPreview renders with fillWidth → the ENTIRE page is scaled
 *     to fit (no center crop, no viewport capture, no editor zoom influence).
 *   - A stored raster (thumbnail / thumbnail_url) overlays the live render only
 *     after blank-detection passes; otherwise the live render shows through.
 *
 * @param {object} props
 * @param {object} props.design        design row from GET /api/designs
 * @param {React.ReactNode} props.placeholder shown only when there is no page
 *                          content AND no usable stored thumbnail
 * @param {string} [props.className]   forwarded to the thumbnail container
 * @param {function} [props.onClick]
 * @param {React.ReactNode} [props.children] overlay slot (hover CTAs, badges)
 */
export default function DesignPreview({ design, placeholder = null, className = '', onClick, children }) {
  const cw = design?.canvas_size?.width || design?.canvas_width || 1080;
  const ch = design?.canvas_size?.height || design?.canvas_height || 1080;

  // Map design → the template shape TemplateThumbnail consumes.
  //  - template_json.pages   ← design.pages (top-level) OR design.template_json.pages
  //    (the designer-templates endpoint returns the design with a synthesized
  //    template_json; legacy /api/designs rows carry pages at the top level).
  //  - thumbnail             ← design.thumbnail (inline data URL)
  //  - preview_url           ← design.thumbnail_url (hosted, second candidate)
  const pages = Array.isArray(design?.pages)
    ? design.pages
    : (Array.isArray(design?.template_json?.pages) ? design.template_json.pages : []);
  const asTemplate = {
    id: design?.id,
    name: design?.name,
    canvas_width: cw,
    canvas_height: ch,
    template_json: { pages },
    thumbnail: design?.thumbnail || null,
    preview_url: design?.thumbnail_url || null,
  };

  return (
    <TemplateThumbnail
      template={asTemplate}
      className={className}
      onClick={onClick}
      fallback={placeholder}
    >
      {children}
    </TemplateThumbnail>
  );
}

/**
 * True when DesignPreview will render a real preview (stored thumbnail OR a
 * page that has content) rather than only the placeholder. TemplateThumbnail
 * renders the live page whenever a first page exists, so any page — even an
 * empty one with just a background — counts as a real preview.
 */
export function hasDesignPreview(design) {
  if (resolveDesignThumbnail(design)) return true;
  const pages = Array.isArray(design?.pages)
    ? design.pages
    : (Array.isArray(design?.template_json?.pages) ? design.template_json.pages : []);
  return !!pages[0];
}
